"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { MarketId, Timeframe } from "@/stores/tradeStore";
import { MARKET_CONFIG, type CandleData } from "@/lib/hyperliquid";

const WS_URL = "wss://api.hyperliquid.xyz/ws";

const PING_INTERVAL = 30_000;
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
const MAX_RECONNECT_ATTEMPTS = 10;

// Distinct dexes we need allMids subscriptions for
const ALL_DEXES: (string | undefined)[] = [undefined, "cash", "xyz"];

interface UseHyperliquidSocketOptions {
  market: MarketId;
  timeframe: Timeframe;
  onCandle?: (candle: CandleData) => void;
  onMidPrices?: (prices: Record<string, string>, dex?: string) => void;
  enabled?: boolean;
}

interface UseHyperliquidSocketReturn {
  isConnected: boolean;
  reconnect: () => void;
}

const hlIntervalToTimeframe = (interval: string): Timeframe | null => {
  const map: Record<string, Timeframe> = {
    "1m": "1m",
    "5m": "5m",
    "15m": "15m",
    "1h": "1h",
    "4h": "4h",
    "1d": "1d",
    "1w": "1w",
    "1M": "1M",
  };
  return map[interval] ?? null;
};

const getCoinForWs = (m: MarketId): string => MARKET_CONFIG[m].coin;

export function useHyperliquidSocket(
  options: UseHyperliquidSocketOptions
): UseHyperliquidSocketReturn {
  const { market, timeframe, onCandle, onMidPrices, enabled = true } = options;

  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const pingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intentionalCloseRef = useRef(false);
  const connectRef = useRef<(() => void) | undefined>(undefined);

  // Callback refs to avoid stale closures
  const onCandleRef = useRef(onCandle);
  const onMidPricesRef = useRef(onMidPrices);
  useEffect(() => {
    onCandleRef.current = onCandle;
  }, [onCandle]);
  useEffect(() => {
    onMidPricesRef.current = onMidPrices;
  }, [onMidPrices]);

  // Track current subscription so we can unsub/resub on change
  const currentSubRef = useRef<{ coin: string; interval: string } | null>(null);
  const marketRef = useRef(market);
  const timeframeRef = useRef(timeframe);
  useEffect(() => {
    marketRef.current = market;
  }, [market]);
  useEffect(() => {
    timeframeRef.current = timeframe;
  }, [timeframe]);

  // Helper: send JSON over the open socket
  const sendJson = useCallback((msg: unknown) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const subscribeCandleSub = useCallback(
    (coin: string, interval: string) => {
      sendJson({ method: "subscribe", subscription: { type: "candle", coin, interval } });
      currentSubRef.current = { coin, interval };
    },
    [sendJson]
  );

  const unsubscribeCandleSub = useCallback(() => {
    if (currentSubRef.current) {
      sendJson({
        method: "unsubscribe",
        subscription: {
          type: "candle",
          coin: currentSubRef.current.coin,
          interval: currentSubRef.current.interval,
        },
      });
      currentSubRef.current = null;
    }
  }, [sendJson]);

  // Connection lifecycle — stored in a ref so onclose can self-reference
  // without creating a useCallback dependency cycle.
  useEffect(() => {
    connectRef.current = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) return;

      intentionalCloseRef.current = false;
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        reconnectAttemptRef.current = 0;

        // Subscribe candle for current market + timeframe
        const coin = getCoinForWs(marketRef.current);
        const interval: string = timeframeRef.current;
        ws.send(
          JSON.stringify({
            method: "subscribe",
            subscription: { type: "candle", coin, interval },
          })
        );
        currentSubRef.current = { coin, interval };

        // Subscribe allMids for every dex
        for (const dex of ALL_DEXES) {
          const sub: Record<string, unknown> = { type: "allMids" };
          if (dex) sub.dex = dex;
          ws.send(JSON.stringify({ method: "subscribe", subscription: sub }));
        }

        // Keepalive ping
        if (pingRef.current) clearInterval(pingRef.current);
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ method: "ping" }));
          }
        }, PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          if (msg.channel === "candle") {
            const d = msg.data;
            if (!d) return;
            const tf = hlIntervalToTimeframe(d.i);
            if (!tf || tf !== timeframeRef.current) return;
            // Reject stale candles from previous market subscription
            if (d.s !== getCoinForWs(marketRef.current)) return;

            const candle: CandleData = {
              time: Math.floor(d.t / 1000),
              open: parseFloat(d.o),
              high: parseFloat(d.h),
              low: parseFloat(d.l),
              close: parseFloat(d.c),
              volume: parseFloat(d.v),
            };
            onCandleRef.current?.(candle);
          } else if (msg.channel === "allMids") {
            const mids = msg.data?.mids;
            if (mids) {
              onMidPricesRef.current?.(mids, msg.data?.dex);
            }
          }
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        if (pingRef.current) {
          clearInterval(pingRef.current);
          pingRef.current = null;
        }
        currentSubRef.current = null;

        if (
          !intentionalCloseRef.current &&
          reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS
        ) {
          const delay = Math.min(
            RECONNECT_BASE_MS * 2 ** reconnectAttemptRef.current,
            RECONNECT_MAX_MS
          );
          reconnectAttemptRef.current += 1;
          reconnectTimeoutRef.current = setTimeout(() => connectRef.current?.(), delay);
        }
      };

      ws.onerror = () => {
        // onclose fires after this — reconnection handled there
      };
    };
  });

  // Initial connection + cleanup
  useEffect(() => {
    if (!enabled) return;
    connectRef.current?.();

    return () => {
      intentionalCloseRef.current = true;
      if (pingRef.current) clearInterval(pingRef.current);
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      wsRef.current?.close();
    };
  }, [enabled]);

  // Handle market/timeframe changes — unsub old, sub new
  useEffect(() => {
    if (!isConnected) return;

    const newCoin = getCoinForWs(market);
    const newInterval: string = timeframe;

    const current = currentSubRef.current;
    if (current?.coin === newCoin && current?.interval === newInterval) return;

    unsubscribeCandleSub();
    subscribeCandleSub(newCoin, newInterval);
  }, [market, timeframe, isConnected, unsubscribeCandleSub, subscribeCandleSub]);

  const reconnect = useCallback(() => {
    intentionalCloseRef.current = true;
    wsRef.current?.close();
    reconnectAttemptRef.current = 0;
    setTimeout(() => connectRef.current?.(), 100);
  }, []);

  return { isConnected, reconnect };
}
